import React from "react";
import { motion } from "framer-motion";
import { components } from "ComponentRenderer.js";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, Content2Xl, ContentWithVerticalPadding } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro";
import { LogoLink } from "components/headers/HeaderLight";
import { SectionHeading as HeadingBase } from "components/misc/Headings";
import { SectionDescription as DescriptionBase } from "components/misc/Typography";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { Link, useNavigate } from 'react-router-dom'
import { ReactComponent as CheckboxIcon } from "feather-icons/dist/icons/check-circle.svg";
import screenshotImageSrc from "images/demo/mainPage.png";
import logo from "images/logo.svg";
import DownloadApp from "components/cta/DownloadApp.js";
import Footer from "components/footers/FourColumnWithInputForm.js";
import { ROUTE_PATHS } from "utils/routes";
import { Button } from "reactstrap";
import { useAuth } from "hooks/useAuth";
import PropTypes from 'prop-types';

const Row = tw.div`flex`;
const NavRow = tw(Row)`flex flex-col lg:flex-row items-center justify-between`;
const NavLink = tw.a`mt-4 lg:mt-0 transition duration-300 font-medium pb-1 border-b-2 mr-12 text-gray-700 border-gray-400 hocus:border-gray-700`;
const PrimaryNavLink = tw(
  NavLink
)`text-gray-100 bg-primary-500 px-6 py-3 border-none rounded hocus:bg-primary-900 focus:shadow-outline mt-6 md:mt-4 lg:mt-0`;
const HeroRow = tw(Row)`flex-col lg:flex-row justify-between items-center pt-8 lg:pt-12 pb-16 max-w-screen-2xl mx-auto flex-wrap`;
const Column = tw.div`flex-1`;
const TextColumn = tw(Column)`mx-auto lg:mr-0 max-w-2xl lg:max-w-xl xl:max-w-2xl flex-shrink-0`;
const Heading = tw(HeadingBase)`text-center lg:text-left text-primary-900 leading-snug`;
const Description = tw(
  DescriptionBase
)`mt-4 text-center lg:text-left lg:text-base text-gray-700 max-w-lg mx-auto lg:mx-0`;
const Actions = tw.div`flex flex-col sm:flex-row justify-center lg:justify-start`;
const PrimaryButton = tw(
  Button
)`px-8 py-3 font-bold rounded bg-primary-500 text-gray-100 hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-outline focus:outline-none transition duration-300 mt-12 inline-block tracking-wide text-center px-10 py-4 font-semibold tracking-normal`;
const FeatureList = tw.ul`mt-6 leading-loose flex flex-wrap max-w-xl mx-auto lg:mx-0`;
const Feature = tw.li`mt-2 flex items-center flex-shrink-0 w-full sm:w-1/2 justify-center lg:justify-start`;
const FeatureIcon = tw(CheckboxIcon)`w-5 h-5 text-primary-500`;
const FeatureText = tw.p`ml-2 font-medium text-gray-700`;
const ImageColumn = tw(Column)`mx-auto lg:mr-0 relative mt-16 lg:mt-0 lg:ml-8`;
const ImageContainer = tw.div``;
const Image = tw.img`max-w-full rounded-t sm:rounded`;
const SectionContainer = tw(ContentWithVerticalPadding)``;
const SectionHeading = tw(HeadingBase)`text-primary-900`;
const SectionDescription = tw(DescriptionBase)`text-center mx-auto text-gray-600 max-w-4xl`;
const PreviewCards = tw.div`flex flex-wrap -mr-12`;
const PreviewCardContainer = tw.div`mt-24 mx-auto md:mx-0 max-w-lg w-full md:w-1/2 lg:w-1/3 pr-12`;
const PreviewCard = tw(motion.a)`block rounded-lg shadow-raised`;
const PreviewCardImageContainer = tw.div`rounded-t-lg border-0 border-b-0`;
const PreviewCardImage = styled(motion.div)`
  ${props => css`background-image: url("${props.$imageSrc}");`}
  ${tw`h-72 md:h-128 bg-white rounded-t-lg flex items-center justify-center`}
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;
const PreviewButton = tw(PrimaryButtonBase)`w-full rounded-b-lg rounded-t-none py-5 font-semibold`;
const HighlightedTextInverse = tw.span`bg-gray-100 text-primary-500 px-4 transform -skew-x-12 inline-block`;

  const previewImageAnimationVariants = {
    rest: {
      backgroundPositionY: "0%"
    },
    hover: {
      backgroundPositionY: "100%",
      transition: { type: "tween", ease: "linear", duration: 5 }
    }
  };

  const MainLandingPage = ({
    features = null,
    primaryButtonUrl = ROUTE_PATHS.FEATURES,
    primaryButtonText = "Descubre todas las funciones",
    buttonRoundedCss = "",
    landingPages = components.landingPages,
    innerPages = components.innerPages,
    blocks = components.blocks,
    heading = "Conexa Home",
  description = "La plataforma integral para la administración segura de tu fraccionamiento. Control de acceso con QR anti-fraude, registro de visitantes, pagos, amenidades y comunicación — todo en un solo portal, accesible desde cualquier dispositivo."
  }) => {

      const { user, isAdmin, isUser, logout, initializing } = useAuth();
      const navigate = useNavigate();

      // Define role-based features
      const userFeatures = [
        "Invitaciones QR para visitantes (cambia cada 30s)",
        "Acceso biométrico por reconocimiento facial",
        "Apertura automática de barrera — sin caseta",
        "Verificación de identidad OTP por WhatsApp/SMS",
        "Consulta de adeudos y pagos en línea",
        "Reservación de amenidades (alberca, salón, canchas)",
        "Anuncios y comunicados del fraccionamiento",
        "Documentación y reglamento siempre disponibles",
        "Historial de visitas y bitácora de accesos",
        "Notificaciones en tiempo real"
      ];

      const adminFeatures = [
        "Panel de administración centralizado",
        "Gestión de propietarios y residentes",
        "Control y reportes de pagos y adeudos",
        "Administración de amenidades y reservas",
        "Bitácora completa de accesos y visitantes",
        "Reportes financieros exportables",
        "Gestión de anuncios y circulares masivas",
        "Catálogos y configuración del fraccionamiento",
        "Panel de contabilidad avanzada",
        "Control de roles y permisos del sistema"
      ];

      // Set features based on user role or use default
      if (!features) {
        if (user && isAdmin()) {
          features = adminFeatures;
        } else {
          features = userFeatures;
        }
      }

      // Set pages to display based on role
      let pagesToShow = landingPages;
      if (user && isAdmin()) {
        pagesToShow = components.adminPages || landingPages;
      }

      // Role-based heading and description
      let roleBasedHeading = heading;
      let roleBasedDescription = description;
      
      if (user && isAdmin()) {
        roleBasedHeading = "Panel de Administración — Conexa Home";
        roleBasedDescription = "Centro de control administrativo para la gestión completa de tu fraccionamiento. Propietarios, pagos, amenidades, accesos y reportes — todo en un solo lugar.";
      } else if (user && isUser()) {
        roleBasedHeading = "Mi Portal — Conexa Home";
        roleBasedDescription = "Tu portal personal para gestionar visitas con QR, consultar pagos, reservar amenidades y mantenerte informado de todo lo que ocurre en tu fraccionamiento.";
      }

      let buttonLoginText = "Inicio de sesion";

      if (user !== null) {
        buttonLoginText = "Cerrar Sesión";
      }

      async function buttonAction(event) {
        event.preventDefault();
        if (user === null) {
          navigate("./inicio-sesion", { replace: true });
        }
        else {
          logout();
        }
      }

      return (
        <AnimationRevealPage disabled>
          <Container tw="bg-gray-100 -mx-8 -mt-8 pt-8 px-8">
            <Content2Xl>
              <NavRow>
                <LogoLink href="/">
                  <img src={logo} alt="" />{" "}
                  <span>BizCode GDL</span>
                </LogoLink>
                <div className="flex flex-wrap justify-center lg:justify-end items-center -mr-12">
                  <NavLink href="/contacto">
                    Contacto
                  </NavLink>
                  <NavLink href="/documentos">
                    Doctos
                  </NavLink>
                  <NavLink href="/anuncios">
                    Anuncios
                  </NavLink>
                  {user && isAdmin() && (
                    <NavLink href={ROUTE_PATHS.ADMINDASHBOARD}>
                      Dashboard
                    </NavLink>
                  )}
                  <NavLink href="/features">
                    Características
                  </NavLink>
                  <div className="md:hidden flex-100 h-0"></div>
                  <PrimaryNavLink>
                    <Link onClick={buttonAction}>{buttonLoginText}</Link>
                  </PrimaryNavLink>
                </div>
              </NavRow>
              <HeroRow>
                <TextColumn>
                  <Heading as="h1">{roleBasedHeading}</Heading>
                  <Description>{roleBasedDescription}</Description>
                  <FeatureList>
                    {features.map((feature, index) => (
                      <Feature key={`${feature}-${index}`}>
                        <FeatureIcon />
                        <FeatureText>{feature}</FeatureText>
                      </Feature>
                    ))}
                  </FeatureList>
                  <Actions>
                    <PrimaryButton href={primaryButtonUrl} css={buttonRoundedCss}>
                      {primaryButtonText}
                    </PrimaryButton>
                  </Actions>
                </TextColumn>
                <ImageColumn>
                  <ImageContainer>
                    <Image src={screenshotImageSrc} />
                  </ImageContainer>
                </ImageColumn>
              </HeroRow>
              <SectionContainer id="Modulos">
                <SectionHeading>
                  {user && isAdmin() ? "Módulos Administrativos" : "Módulos disponibles"}
                </SectionHeading>
                  <SectionDescription>
                  {user && isAdmin() 
                    ? "Herramientas administrativas para la gestión completa del fraccionamiento."
                    : "Cada módulo está diseñado para hacer tu vida en el fraccionamiento más fácil, segura y conectada. Solicita acceso a tu administrador."
                  }
                </SectionDescription>
                <PreviewCards>
                  {Object.entries(pagesToShow).map(([pageName, page], index) => (
                    <PreviewCardContainer key={`${page.name}-${index}`}>
                      <PreviewCard initial="rest" animate="rest" whileHover="hover" href={page.url}>
                        <PreviewCardImageContainer>
                          <PreviewCardImage
                            transition={{ type: "tween" }}
                            variants={previewImageAnimationVariants}
                            $imageSrc={page.imageSrc}
                          />
                        </PreviewCardImageContainer>
                        <PreviewButton>
                          {user && isAdmin() ? `Admin - ${page.name}` : `Módulo "${page.name}"`}
                        </PreviewButton>
                      </PreviewCard>
                    </PreviewCardContainer>
                  ))}
                </PreviewCards>
              </SectionContainer>
            </Content2Xl>
          </Container>

          <DownloadApp
            text={<>Próximamente espera por nuestra versión <HighlightedTextInverse>Móvil.</HighlightedTextInverse></>}
          />
          <Footer />
        </AnimationRevealPage>
      );
    };

  MainLandingPage.propTypes = {
    features: PropTypes.array,
    primaryButtonUrl: PropTypes.string,
    primaryButtonText: PropTypes.string,
    buttonRoundedCss: PropTypes.string,
    landingPages: PropTypes.object,
    innerPages: PropTypes.object,
    blocks: PropTypes.object,
    heading: PropTypes.string,
    description: PropTypes.string,
  };

  export default MainLandingPage;
